// user-dashboard/components/Secret.jsx
import React, { useState, useCallback } from "react";

/**
 * Small helper to display and copy secrets (like passwords).
 *
 * Props:
 * - value (string) -> the current secret text
 * - onChange (function) -> callback when editing
 * - editable (boolean) -> if true, shows an input for editing
 * - onBlur (function) -> callback when input loses focus
 * - autoFocus (boolean) -> if true, auto-focuses the input
 */
export default function Secret({ value = "", onChange, editable, onBlur, autoFocus }) {
  const [copyFeedback, setCopyFeedback] = useState(null);

  const copy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(value || "");
      setCopyFeedback("ok");
      setTimeout(() => setCopyFeedback(null), 2000);
    } catch {
      setCopyFeedback("fail");
      setTimeout(() => setCopyFeedback(null), 2000);
    }
  }, [value]);

  return (
    <div className="d-inline-flex align-items-center gap-2">
      {editable ? (
        <input
          type="text"
          className="form-control form-control-sm"
          style={{ width: 180, fontFamily: "monospace" }}
          value={value || ""}
          onChange={e => onChange?.(e.target.value)}
          onBlur={onBlur}
          autoFocus={autoFocus}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.target.blur();
            } else if (e.key === 'Escape' && onBlur) {
              onBlur(e);
            }
          }}
        />
      ) : (
        <span style={{ fontFamily: "monospace" }}>
          {value || ""}
        </span>
      )}
      {!editable && (
        <>
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            onClick={copy}
            title="Copy"
          >
            Copy
          </button>
          {copyFeedback === "ok" && (
            <span className="text-success small" title="Copied to clipboard">✓</span>
          )}
          {copyFeedback === "fail" && (
            <span className="text-danger small">Copy failed</span>
          )}
        </>
      )}
    </div>
  );
}